import { Directive, ElementRef, HostListener, inject, OnInit, AfterViewInit, Renderer2, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Directive to highlight negative numbers in red
 *
 * Works with both native <input> and PrimeNG <p-inputnumber>
 *
 * Usage:
 * <input appNegativeHighlight [formControl]="control" />
 * <p-inputnumber appNegativeHighlight [formControl]="control"></p-inputnumber>
 */
@Directive({
  selector: '[appNegativeHighlight]',
  standalone: true
})
export class NegativeHighlightDirective implements OnInit, AfterViewInit, OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private ngControl = inject(NgControl, { optional: true });

  private isPrimeNgInputNumber = false;
  private inputElement: HTMLInputElement | null = null;
  private valueSubscription: Subscription | null = null;
  private mutationObserver: MutationObserver | null = null;

  ngOnInit(): void {
    // Detect if this is a PrimeNG InputNumber component
    this.isPrimeNgInputNumber = this.el.nativeElement.tagName.toLowerCase() === 'p-inputnumber';
  }

  ngAfterViewInit(): void {
    // Get the actual input element (either native or nested in p-inputnumber)
    this.inputElement = this.isPrimeNgInputNumber
      ? this.el.nativeElement.querySelector('input')
      : this.el.nativeElement;

    // Add input event listener to the actual input element for p-inputnumber
    if (this.isPrimeNgInputNumber && this.inputElement) {
      this.inputElement.addEventListener('input', () => this.handleInput());
      // Also listen for keyup to catch negative sign typing
      this.inputElement.addEventListener('keyup', () => this.handleInput());
      // Listen for blur to catch final value
      this.inputElement.addEventListener('blur', () => this.handleInput());

      // Set up MutationObserver to watch for programmatic changes (e.g., setValue with emitEvent: false)
      // This is necessary for calculated/disabled fields that get updated without triggering valueChanges
      this.setupMutationObserver();
    }

    // Subscribe to form control value changes - do this in AfterViewInit to ensure control is ready
    // Use setTimeout to let Angular finish initializing the form control
    setTimeout(() => {
      if (this.ngControl?.control && !this.valueSubscription) {
        this.valueSubscription = this.ngControl.control.valueChanges.subscribe((value) => {
          this.updateColorFromValue(value);
        });
        // Initial color check
        this.updateColorFromValue(this.ngControl.control.value);
      } else if (this.inputElement) {
        // Fallback: check the input value directly
        this.updateColorFromString(this.inputElement.value);
      }

      // For calculated fields that are set programmatically with emitEvent: false,
      // we need to do a delayed check after Angular's change detection has completed
      // This catches values set during component initialization
      setTimeout(() => this.handleInput(), 500);
    }, 0);
  }

  ngOnDestroy(): void {
    this.valueSubscription?.unsubscribe();
    this.mutationObserver?.disconnect();
  }

  /**
   * Set up MutationObserver to watch for attribute changes on the input element.
   * This catches programmatic value changes that don't emit valueChanges events,
   * such as when setValue() is called with { emitEvent: false } on calculated fields.
   */
  private setupMutationObserver(): void {
    if (!this.inputElement) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          // Check aria-valuenow (PrimeNG uses this for the actual numeric value)
          if (mutation.attributeName === 'aria-valuenow' || mutation.attributeName === 'value') {
            this.handleInput();
            break;
          }
        }
      }
    });

    // Observe attribute changes on the input element
    this.mutationObserver.observe(this.inputElement, {
      attributes: true,
      attributeFilter: ['aria-valuenow', 'value']
    });
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    // Only handle for native input elements (p-inputnumber handled via addEventListener)
    if (!this.isPrimeNgInputNumber) {
      this.handleInput();
    }
  }

  @HostListener('onInput', ['$event'])
  onPrimeNgInput(event: any): void {
    // Handle PrimeNG's onInput event
    if (this.isPrimeNgInputNumber) {
      this.updateColorFromValue(event.value);
    }
  }

  private handleInput(): void {
    if (!this.inputElement) return;

    // Let other directives finish processing first
    setTimeout(() => {
      // For p-inputnumber, check aria-valuenow which contains the actual numeric value
      if (this.isPrimeNgInputNumber) {
        const ariaValue = this.inputElement?.getAttribute('aria-valuenow');
        if (ariaValue) {
          const numValue = parseFloat(ariaValue);
          if (!isNaN(numValue)) {
            this.setColor(numValue < 0 ? 'red' : '');
            return;
          }
        }

        // Fallback: check the form control value directly (useful for disabled calculated fields)
        if (this.ngControl?.control) {
          const controlValue = this.ngControl.control.value;
          if (controlValue !== null && controlValue !== undefined) {
            const numValue = typeof controlValue === 'number' ? controlValue : parseFloat(controlValue);
            if (!isNaN(numValue)) {
              this.setColor(numValue < 0 ? 'red' : '');
              return;
            }
          }
        }
      }

      // Fallback to checking displayed value
      const value = this.inputElement?.value;
      this.updateColorFromString(value || '');
    }, 0);
  }

  private updateColorFromValue(value: any): void {
    if (value === null || value === undefined) {
      this.setColor('');
      return;
    }

    const numValue = typeof value === 'number' ? value : parseFloat(value);
    this.setColor(numValue < 0 ? 'red' : '');
  }

  private updateColorFromString(value: string): void {
    if (!value || value === '-') {
      this.setColor('');
      return;
    }

    // Remove grouping separators (commas) for parsing formatted numbers like "-332,340"
    const cleanedValue = value.replace(/,/g, '');

    // Check if it's a valid number (with optional negative sign and decimals)
    const isValidNumber = /^-?\d+$/.test(cleanedValue) || /^-?\d+\.\d+$/.test(cleanedValue);
    if (!isValidNumber) {
      // Also check if it just starts with a minus sign (user is typing negative number)
      if (value.startsWith('-')) {
        this.setColor('red');
        return;
      }
      this.setColor('');
      return;
    }

    const num = Number(cleanedValue);
    this.setColor(num < 0 ? 'red' : '');
  }

  private setColor(color: string): void {
    const isNegative = color === 'red';

    // For p-inputnumber, we need to style the inner input element
    if (this.isPrimeNgInputNumber) {
      // Try to get the input element if not already cached
      if (!this.inputElement) {
        this.inputElement = this.el.nativeElement.querySelector('input');
      }

      if (this.inputElement) {
        if (isNegative) {
          this.inputElement.style.setProperty('color', 'red', 'important');
          this.inputElement.classList.add('negative-value');
        } else {
          this.inputElement.style.removeProperty('color');
          this.inputElement.classList.remove('negative-value');
        }
      }

      // Also add class to host for CSS targeting
      if (isNegative) {
        this.el.nativeElement.classList.add('has-negative-value');
      } else {
        this.el.nativeElement.classList.remove('has-negative-value');
      }
    } else {
      // Native input element
      if (isNegative) {
        this.el.nativeElement.style.setProperty('color', 'red', 'important');
        this.el.nativeElement.classList.add('negative-value');
      } else {
        this.el.nativeElement.style.removeProperty('color');
        this.el.nativeElement.classList.remove('negative-value');
      }
    }
  }
}
